<template>
  <div>
    <div class="px-3">
      <h3 class="text-medium-1 mb-3">Filter goal templates by:</h3>
      <templates-filters
        class="mx-n2"
        :filter-data="filterData"
        :roles="roles"
        :teams="teams"
        :companies="companies"
        @change="onFilter"
      />
    </div>
    <div class="goals-list mt-4">
      <goal-card-skeleton v-if="loadingState === LoadingState.LOADING" />
      <template v-else-if="loadingState === LoadingState.LOADED && goals && goals.length > 0">
        <goal-template-row
          @select="$emit('select', $event)"
          class="mb-3"
          v-for="goal in goals"
          :goal="goal"
          :key="goal.id"
          :all-teams="teams"
        />
      </template>
      <div class="flex-center mt-5" v-else-if="loadingState === LoadingState.LOADED">
        <info-box>
          <template slot="message">
            <div>
              There is currently no <b>Goals</b> to see here.
              <br />
              Try changing your filters.
            </div>
          </template>
        </info-box>
      </div>
      <div
        class="text-secondary flex-center mt-5"
        v-else-if="loadingState === LoadingState.ERRORED"
      >
        Something went wrong. Please try again.
      </div>
    </div>
  </div>
</template>

<script>
import { trackAnalyticsEvent } from '@/analytics'
import InfoBox from '@/components/InfoBox'
import GoalCardSkeleton from '@/components/skeletons/GoalCardSkeleton'
import GoalTemplateRow from '@/components/templates/GoalTemplateRow'
import TemplatesFilters from '@/components/templates/TemplatesFilters'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { LoadingState } from '@/constants/common'
import service from '@/service'
import urlManager from '@/urlManager'
import showAPIError from '@/utils/showAPIError'
import * as R from 'ramda'

/**
 * This component shows the list of template goals
 */
export default {
  name: 'GoalTemplates',
  props: ['filterData', 'orgId'],
  components: {
    TemplatesFilters,
    GoalTemplateRow,
    InfoBox,
    GoalCardSkeleton
  },
  data() {
    return {
      roles: [],
      teams: [],
      goals: null,
      companies: [],
      loadingState: LoadingState.LOADING,
      LoadingState,
      prompt: '',
      isPrompted: false
    }
  },
  computed: {},
  methods: {
    async onPromptSubmit() {
      try {
        trackAnalyticsEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.SEARCH_NORTH_AI, EVENT_NAME.GOAL)
        this.$emit('prompt')
        this.isPrompted = true
        this.loadingState = LoadingState.LOADING
        const { data } = await service.client.post(urlManager.generateGoals(this.orgId), {
          prompt: this.prompt
        })
        this.goals = data
        this.loadingState = LoadingState.LOADED
      } catch (e) {
        showAPIError(e)
        this.loadingState = LoadingState.ERRORED
      }
    },
    onFilter(event) {
      this.prompt = ''
      this.$emit('filter', event)
    },
    async fetchRoles() {
      try {
        const { data } = await service.client.get(urlManager.roleTemplates)
        this.roles = data.sort((a, b) => a.title.localeCompare(b.title))
      } catch (e) {
        showAPIError(e)
      }
    },
    async fetchCompanies() {
      try {
        const { data } = await service.client.get(urlManager.companyTemplates)
        this.companies = data.sort((a, b) => a.title.localeCompare(b.title))
      } catch (e) {
        showAPIError(e)
      }
    },
    async fetchTeams() {
      try {
        const { data } = await service.client.get(urlManager.functionTemplates)
        this.teams = data.sort((a, b) => a.title.localeCompare(b.title))
      } catch (e) {
        showAPIError()
      }
    },
    fetchFilterOptions() {
      this.fetchTeams()
      this.fetchRoles()
      this.fetchCompanies()
    },
    async refreshGoals() {
      const params = {}
      this.loadingState = LoadingState.LOADING
      if (this.filterData.roleIds && +this.filterData.roleIds !== -1) {
        params.role_ids = this.filterData.roleIds
      }
      if (this.filterData.teamIds && +this.filterData.teamIds !== -1) {
        params.function_ids = this.filterData.teamIds
      }
      if (this.filterData.companyIds && +this.filterData.companyIds !== -1) {
        params.company_ids = this.filterData.companyIds
      }
      try {
        const { data } = await service.client.get(urlManager.goalTemplates, {
          params
        })
        this.goals = data
        this.loadingState = LoadingState.LOADED
      } catch (e) {
        showAPIError(e)
        this.loadingState = LoadingState.ERRORED
      }
    }
  },
  mounted() {
    this.fetchFilterOptions()
    this.refreshGoals()
  },
  watch: {
    filterData(newValue, oldValue) {
      !R.equals(newValue, oldValue) && !this.prompt && this.refreshGoals()
    }
  }
}
</script>

<style lang="scss" scoped></style>
